import routes from 'config/routes'
import { fetch } from 'utils/fetch'

type FetcherResult =
  | { ok: true; data: Record<string, unknown> }
  | { ok: false; code: string; retryable: boolean; message: string }

type FetcherFailure = Extract<FetcherResult, { ok: false }>

// Maps a *real* HTTP response status to a stable CMS error code. 500 is deliberately absent:
// `utils/fetch` reports a connection-level failure (no response, timeout) as status 500 via
// `responseStatus || 500`, so a 500 here is ambiguous between a genuine server error and a
// network drop. We can't tell them apart on the client, so 500 falls through to the retryable
// NETWORK code below. (BAD_CONFIG is still classified server-side, where `fetchByUrl` sees the
// real ContentStack response — see server/routes/cms.ts.)
const CMS_ERROR_BY_STATUS: Record<number, Pick<FetcherFailure, 'code' | 'retryable'>> = {
  404: { code: 'NOT_FOUND', retryable: false },
  429: { code: 'RATE_LIMITED', retryable: true },
  502: { code: 'UPSTREAM_5XX', retryable: true },
  504: { code: 'TIMEOUT', retryable: true },
}

function getFetchErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined
  }

  const { status } = error as { status?: unknown }

  return typeof status === 'number' ? status : undefined
}

async function fetchCmsEntry(contentType: string, pageUrl: string): Promise<FetcherResult> {
  try {
    // We pass `useMenuService = true` for two reasons unrelated to the menu
    // service: it tells `utils/fetch` to (a) skip the snake_case→camelCase
    // mutation in JSONParse so ContentStack uids reach `BLOCK_REGISTRY`
    // unchanged, and (b) read the response from the `{ data }` envelope our
    // Koa /cms/entry route returns. Naming is misleading but it's the existing
    // escape hatch — revisit when we stop using the utils/fetch complex for CMS requests.
    const response = (await fetch(
      null,
      routes.cms.entry,
      { contentType, pageUrl },
      'GET',
      'default',
      {},
      null,
      false,
      true,
    )) as { data?: Record<string, unknown> } | null

    if (response?.data) {
      return { ok: true, data: response.data }
    }

    return {
      ok: false,
      code: 'BAD_RESPONSE',
      retryable: false,
      message: 'CMS response missing data',
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    const status = getFetchErrorStatus(error)
    const cmsError = status ? CMS_ERROR_BY_STATUS[status] : undefined

    if (cmsError) {
      return { ok: false, ...cmsError, message }
    }

    return { ok: false, code: 'NETWORK', retryable: true, message }
  }
}

export { fetchCmsEntry }
